import * as Dialog from '@radix-ui/react-dialog';
import { X, AlertCircle } from 'lucide-react';
import type { ValidationError } from '../../schemas/sceneSchema';

interface ValidationErrorDisplayProps {
  isOpen: boolean;
  onClose: () => void;
  errors: ValidationError[];
  title?: string;
}

/**
 * Dialog component for displaying validation errors in user-friendly format
 * Shows field paths, error types, current values, and suggested fixes
 */
export function ValidationErrorDisplay({
  isOpen,
  onClose,
  errors,
  title = 'Scene Import Errors',
}: ValidationErrorDisplayProps) {
  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content
          className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2
                     bg-dark-panel border panel-border rounded-lg shadow-xl
                     max-w-2xl w-full max-h-[80vh] overflow-hidden z-50"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b panel-border">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/10">
                <AlertCircle className="w-5 h-5 text-red-400" />
              </div>
              <Dialog.Title className="text-lg font-semibold text-dark-text">
                {title}
              </Dialog.Title>
            </div>
            <Dialog.Close asChild>
              <button
                className="p-2 rounded-lg hover:bg-dark-border transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-dark-muted" />
              </button>
            </Dialog.Close>
          </div>

          {/* Error List */}
          <div className="px-6 py-4 overflow-y-auto max-h-[calc(80vh-120px)]">
            <p className="text-sm text-dark-muted mb-4">
              The following errors were found in the scene file. Please fix
              these issues and try again.
            </p>

            <div className="space-y-4">
              {errors.map((error, index) => (
                <div
                  key={index}
                  className="p-4 rounded-lg bg-red-500/5 border border-red-500/20"
                >
                  {/* Field path */}
                  <div className="flex items-start gap-2 mb-2">
                    <span className="text-xs font-mono text-red-400 bg-red-500/10 px-2 py-1 rounded">
                      {error.path || 'root'}
                    </span>
                    <span className="text-xs text-dark-muted">
                      {error.code}
                    </span>
                  </div>

                  {/* Error message */}
                  <p className="text-sm text-dark-text mb-2">{error.message}</p>

                  {/* Current value (if available) */}
                  {error.received !== undefined && (
                    <div className="mb-2">
                      <span className="text-xs text-dark-muted">
                        Received:{' '}
                      </span>
                      <code className="text-xs font-mono text-red-300 bg-red-500/10 px-1.5 py-0.5 rounded">
                        {String(error.received)}
                      </code>
                    </div>
                  )}

                  {/* Expected value (if available) */}
                  {error.expected && (
                    <div className="mb-2">
                      <span className="text-xs text-dark-muted">
                        Expected:{' '}
                      </span>
                      <code className="text-xs font-mono text-green-300 bg-green-500/10 px-1.5 py-0.5 rounded">
                        {error.expected}
                      </code>
                    </div>
                  )}

                  {/* Suggestion (if available) */}
                  {error.suggestion && (
                    <div className="mt-2 pt-2 border-t border-red-500/20">
                      <span className="text-xs font-medium text-yellow-400">
                        Suggestion:{' '}
                      </span>
                      <span className="text-xs text-dark-text">
                        {error.suggestion}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t panel-border">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-body-head hover:bg-body-head/80
                       text-white text-sm font-medium transition-colors"
            >
              Close
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
